import React, { useEffect, useMemo } from "react";
import { Path, PathRelationship } from "../../services/mechanismsClient";
import { DataGrid } from "@mui/x-data-grid";
import { useGridApiRef } from "@mui/x-data-grid";
import { DataGridToolbar } from "../DataGridToolbar";
import { useTheme } from "../../theme/useTheme";

function relationshipEndpoints(
  rel: PathRelationship,
): { source: string; target: string } | null {
  const source = rel.source ?? rel.startNode ?? rel.from;
  const target = rel.target ?? rel.endNode ?? rel.to;
  if (!source || !target) return null;
  return { source, target };
}

/** Edge list aligned with `PathwayGraph` / cytoscape (deduped undirected gene–gene pairs). */
function buildPathwayEdgeRows(pathData: Path): {
  id: string;
  source: string;
  relationship: string;
  target: string;
}[] {
  const nodes = pathData.nodes;
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const labelOf = (id: string) => nodeById.get(id)?.caption?.trim() || id;

  const geneNodes = nodes.filter((n) => n.properties?.node_type === "Gene");
  const diseaseNodes = nodes.filter((n) => n.properties?.node_type !== "Gene");

  const edgeKey = (a: string, b: string) => (a < b ? `${a}::${b}` : `${b}::${a}`);
  const seen = new Set<string>();
  const rows: { id: string; source: string; relationship: string; target: string }[] = [];

  const addEdge = (sourceId: string, targetId: string, caption: string) => {
    if (sourceId === targetId) return;
    const k = edgeKey(sourceId, targetId);
    if (seen.has(k)) return;
    seen.add(k);
    rows.push({
      id: k,
      source: labelOf(sourceId),
      relationship: caption,
      target: labelOf(targetId),
    });
  };

  if (pathData.relationships?.length) {
    pathData.relationships.forEach((rel: PathRelationship) => {
      const ends = relationshipEndpoints(rel);
      if (!ends) return;
      const cap = rel.caption || rel.type || "RELATED";
      addEdge(ends.source, ends.target, cap);
    });
  }

  geneNodes.forEach((g1, i) => {
    geneNodes.forEach((g2, j) => {
      if (i >= j) return;
      const a = g1.id;
      const b = g2.id;
      const source = a < b ? a : b;
      const target = a < b ? b : a;
      addEdge(source, target, "INTERACTS_WITH");
    });
  });

  if (!pathData.relationships?.length) {
    geneNodes.forEach((g) => {
      diseaseNodes.forEach((d) => addEdge(g.id, d.id, "ASSOCIATED_WITH"));
    });
  }

  return rows;
}

// DataGridToolbar is now imported from ../DataGridToolbar
interface PathwayTableProps {
  selectedPathway: string;
  pathData: Path | undefined;
}

export const PathwayTable: React.FC<PathwayTableProps> = ({
  selectedPathway,
  pathData,
}) => {
  const themeCtx = useTheme();
  const nodesApiRef = useGridApiRef();
  const edgesApiRef = useGridApiRef();
  const [tableEntity, setTableEntity] = React.useState<"nodes" | "edges">("nodes");
  const [fullscreen, setFullscreen] = React.useState(false);
  const [searchActive, setSearchActive] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");

  const toolbarApiRef = tableEntity === "nodes" ? nodesApiRef : edgesApiRef;

  useEffect(() => {
    setSearchValue("");
    setSearchActive(false);
  }, [tableEntity]);

  useEffect(() => {
    setTableEntity("nodes");
  }, [selectedPathway]);

  const rows = useMemo(
    () =>
      pathData?.nodes
        ? pathData.nodes.map((node: any, idx: number) => ({
          id: node.id || idx,
          name: node.caption,
          type: node.properties?.node_type || "Unknown",
          description:
            node.properties?.description || "No description available",
        }))
        : [],
    [pathData],
  );

  const edgeRows = useMemo(
    () => (pathData ? buildPathwayEdgeRows(pathData) : []),
    [pathData],
  );

  // Filter rows by search value
  const filteredRows = useMemo(() => {
    if (!searchValue.trim()) return rows;
    const val = searchValue.trim().toLowerCase();
    return rows.filter(
      (row: any) =>
        row.name.toLowerCase().includes(val) ||
        row.type.toLowerCase().includes(val) ||
        row.description.toLowerCase().includes(val),
    );
  }, [rows, searchValue]);

  const filteredEdgeRows = useMemo(() => {
    if (!searchValue.trim()) return edgeRows;
    const val = searchValue.trim().toLowerCase();
    return edgeRows.filter(
      (row) =>
        row.source.toLowerCase().includes(val) ||
        row.relationship.toLowerCase().includes(val) ||
        row.target.toLowerCase().includes(val),
    );
  }, [edgeRows, searchValue]);

  const columns = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 180,
      resizable: true,
      cellClassName: "border-r border-gray-200",
      // headerClassName: "border-r border-gray-400",
    },
    {
      field: "type",
      headerName: "Type",
      flex: 1,
      maxWidth: 100,
      resizable: true,
      cellClassName: "border-r border-gray-200",
      //headerClassName: "border-r border-gray-400",
      renderCell: (params: any) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${params.value === "Gene"
            ? "bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200"
            : params.value === "Disease"
              ? "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200"
              : "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            }`}
        >
          {params.value}
        </span>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 2,
      minWidth: 280,
      resizable: true,
      cellClassName: "border-r border-gray-200",
      //headerClassName: "border-r border-gray-400",
      renderCell: (params: any) => (
        <div className="truncate max-w-md cursor-help" title={params.value}>
          {params.value}
        </div>
      ),
    },
  ];

  const edgeColumns = [
    {
      field: "source",
      headerName: "Source",
      flex: 1.15,
      minWidth: 96,
      maxWidth: 160,
      resizable: true,
      sortable: true,
      cellClassName: "border-r border-gray-200",
    },
    {
      field: "relationship",
      headerName: "Relationship",
      flex: 1,
      minWidth: 160,
      resizable: true,
      sortable: true,
      cellClassName: "border-r border-gray-200",
      renderCell: (params: any) => (
        <span className="inline-flex items-center rounded-md bg-sky-100 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-blue-900 dark:bg-sky-900/50 dark:text-sky-100">
          {params.value}
        </span>
      ),
    },
    {
      field: "target",
      headerName: "Target",
      flex: 1.75,
      minWidth: 280,
      resizable: true,
      sortable: true,
      cellClassName: "border-r border-gray-200",
      renderCell: (params: any) => (
        <div className="truncate max-w-4xl" title={params.value}>
          {params.value}
        </div>
      ),
    },
  ];

  if (!pathData) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        No pathway data available
      </div>
    );
  }

  const gridSx = {
    "& .MuiDataGrid-root": {
      backgroundColor: themeCtx.mode === "dark" ? "#1f2937" : "#fff",
      color: themeCtx.mode === "dark" ? "#e5e7eb" : "#111827",
    },
    "& .MuiDataGrid-cell": {
      borderColor: themeCtx.mode === "dark" ? "#374151" : "#e0e0e0",
      color: themeCtx.mode === "dark" ? "#e5e7eb" : "#111827",
    },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: themeCtx.mode === "dark" ? "#374151" : "#f9fafb",
      borderColor: themeCtx.mode === "dark" ? "#4b5563" : "#bdbdbd",
      color: themeCtx.mode === "dark" ? "#f3f4f6" : "#111827",
    },
    "& .MuiDataGrid-columnHeader": {
      backgroundColor: themeCtx.mode === "dark" ? "#374151" : "#f9fafb",
      color: themeCtx.mode === "dark" ? "#f3f4f6" : "#111827",
      "&:focus": {
        outline: "none",
      },
      "&:focus-within": {
        outline: "none",
      },
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: 600,
      color: themeCtx.mode === "dark" ? "#f3f4f6" : "#111827",
    },
    "& .MuiDataGrid-columnSeparator": {
      color: themeCtx.mode === "dark" ? "#4b5563" : "#e0e0e0",
    },
    "& .MuiDataGrid-columnHeaderTitleContainer": {
      color: themeCtx.mode === "dark" ? "#f3f4f6" : "#111827",
    },
    "& .MuiDataGrid-row": {
      backgroundColor: themeCtx.mode === "dark" ? "#1f2937" : "#fff",
      "&:nth-of-type(odd)": {
        backgroundColor: themeCtx.mode === "dark" ? "#1f2937" : "#f9fafb",
      },
      "&:hover": {
        backgroundColor: themeCtx.mode === "dark" ? "#374151" : "#f3f4f6",
      },
    },
    "& .MuiDataGrid-footerContainer": {
      backgroundColor: themeCtx.mode === "dark" ? "#1f2937" : "#fff",
      borderColor: themeCtx.mode === "dark" ? "#374151" : "#e0e0e0",
    },
    "& .MuiTablePagination-root": {
      color: themeCtx.mode === "dark" ? "#e5e7eb" : "#111827",
    },
    "& .MuiTablePagination-selectIcon": {
      color: themeCtx.mode === "dark" ? "#9ca3af" : "#111827",
    },
    "& .MuiTablePagination-select": {
      color: themeCtx.mode === "dark" ? "#e5e7eb" : "#111827",
    },
    "& .MuiTablePagination-displayedRows": {
      color: themeCtx.mode === "dark" ? "#e5e7eb" : "#111827",
    },
    "& .MuiTablePagination-actions button": {
      color: themeCtx.mode === "dark" ? "#e5e7eb" : "#111827",
      "&.Mui-disabled": {
        color: themeCtx.mode === "dark" ? "#4b5563" : "#9ca3af",
        cursor: "not-allowed",
        opacity: 0.5,
      },
    },
    "& .MuiDataGrid-iconButtonContainer": {
      "& button": {
        color: themeCtx.mode === "dark" ? "#9ca3af" : "#111827",
      },
    },
    "& .MuiDataGrid-menuIconButton": {
      color: themeCtx.mode === "dark" ? "#9ca3af" : "#111827",
    },
    "& .MuiDataGrid-sortIcon": {
      color: themeCtx.mode === "dark" ? "#9ca3af" : "#111827",
    },
  };

  const toolbarSlot = {
    toolbar: (props: object) => (
      <DataGridToolbar
        apiRef={toolbarApiRef}
        fullscreen={fullscreen}
        setFullscreen={setFullscreen}
        searchActive={searchActive}
        setSearchActive={setSearchActive}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        {...props}
      />
    ),
  };

  return (
    <div
      style={{
        width: fullscreen ? "100vw" : "100%",
        height: fullscreen ? "100vh" : "auto",
        position: fullscreen ? "fixed" : "relative",
        top: fullscreen ? 0 : undefined,
        left: fullscreen ? 0 : undefined,
        zIndex: fullscreen ? 1300 : undefined,
        background: fullscreen
          ? themeCtx.mode === "dark"
            ? "#1f2937"
            : "#fff"
          : undefined,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex overflow-hidden rounded-lg border border-gray-200 bg-gray-50 dark:border-slate-600 dark:bg-slate-700">
          <button
            type="button"
            onClick={() => setTableEntity("nodes")}
            className={`px-3 py-1.5 text-sm font-medium transition-colors ${tableEntity === "nodes"
              ? "bg-white text-gray-900 shadow-sm dark:bg-slate-600 dark:text-gray-100"
              : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
              }`}
          >
            Nodes ({rows.length})
          </button>
          <button
            type="button"
            onClick={() => setTableEntity("edges")}
            className={`border-l border-gray-200 px-3 py-1.5 text-sm font-medium transition-colors dark:border-slate-600 ${tableEntity === "edges"
              ? "bg-white text-gray-900 shadow-sm dark:bg-slate-600 dark:text-gray-100"
              : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
              }`}
          >
            Edges ({edgeRows.length})
          </button>
        </div>
      </div>

      <div style={{ flex: "1 1 auto", minHeight: 0, height: "100%" }}>
        {tableEntity === "nodes" ? (
          <DataGrid
            apiRef={nodesApiRef}
            rows={filteredRows}
            columns={columns}
            rowCount={filteredRows.length}
            paginationMode="client"
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25, page: 0 },
              },
            }}
            pageSizeOptions={[10, 25, 50]}
            slots={toolbarSlot}
            disableColumnMenu={false}
            disableRowSelectionOnClick
            autoHeight
            sx={gridSx}
          />
        ) : (
          <DataGrid
            apiRef={edgesApiRef}
            rows={filteredEdgeRows}
            columns={edgeColumns}
            rowCount={filteredEdgeRows.length}
            paginationMode="client"
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25, page: 0 },
              },
            }}
            pageSizeOptions={[10, 25, 50]}
            slots={toolbarSlot}
            disableColumnMenu={false}
            disableRowSelectionOnClick
            autoHeight
            sx={gridSx}
          />
        )}
      </div>
    </div>
  );
};
